import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  /** Wrapper layout classes: `flex-1`, `w-40`, etc. */
  wrapperClassName?: string;
  /** Classes applied to the trigger button. */
  className?: string;
  /**
   * Visual variant:
   * - `default` (bordered, pill-like)
   * - `inline` (text-only trigger that looks like static content until clicked;
   *   chevron only appears on hover. Ideal for inline edit-in-place rows.)
   */
  variant?: 'default' | 'inline';
  /** Placeholder shown when no option matches the current value. */
  placeholder?: React.ReactNode;
  /**
   * Kept compatible with the native <select> signature so existing
   * `onChange={(e) => setX(e.target.value)}` consumers keep working.
   */
  onChange?: (e: { target: { value: string; name?: string } }) => void;
}

type ParsedOption = {
  value: string;
  label: React.ReactNode;
  text: string; // for displaying the selected value on the trigger
  disabled?: boolean;
  key: string | number;
};

function toText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toText).join('');
  if (React.isValidElement(node)) {
    const children = (node.props as { children?: React.ReactNode }).children;
    return toText(children);
  }
  return '';
}

function parseOptions(children: React.ReactNode): ParsedOption[] {
  const out: ParsedOption[] = [];
  React.Children.forEach(children, (child, idx) => {
    if (!React.isValidElement(child)) return;
    // Skip optgroups for now — no usage in the codebase.
    const props = child.props as React.OptionHTMLAttributes<HTMLOptionElement>;
    const value = props.value !== undefined ? String(props.value) : '';
    const label = (props.children ?? '') as React.ReactNode;
    out.push({
      value,
      label,
      text: toText(label),
      disabled: Boolean(props.disabled),
      key: child.key ?? idx,
    });
  });
  return out;
}

export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      className,
      wrapperClassName,
      value,
      defaultValue,
      onChange,
      children,
      disabled,
      id,
      name,
      required,
      variant = 'default',
      placeholder,
      'aria-label': ariaLabel,
    },
    ref,
  ) => {
    const options = React.useMemo(() => parseOptions(children), [children]);

    const [internal, setInternal] = React.useState<string>(
      defaultValue !== undefined ? String(defaultValue) : (value !== undefined ? String(value) : ''),
    );
    const current = value !== undefined ? String(value) : internal;
    const selected = options.find((o) => o.value === current);
    const displayLabel: React.ReactNode =
      selected?.label ?? (placeholder ?? <span className="text-muted-foreground">Select…</span>);

    const isInline = variant === 'inline';

    const select = (v: string) => {
      if (value === undefined) setInternal(v);
      onChange?.({ target: { value: v, ...(name !== undefined ? { name } : {}) } });
    };

    return (
      <div
        className={cn(
          'group relative inline-flex w-full items-center',
          disabled ? 'pointer-events-none opacity-50' : '',
          wrapperClassName,
        )}
      >
        {/* Hidden native input so <label htmlFor> + form submission still work. */}
        <input type="hidden" id={`${id ?? ''}`} name={name} value={current} />

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild disabled={disabled}>
            <button
              ref={ref}
              type="button"
              aria-label={ariaLabel}
              aria-required={required}
              className={cn(
                'peer flex w-full items-center justify-between gap-2 text-left text-sm font-medium text-foreground ring-offset-background transition-[border-color,background-color,box-shadow,color]',
                isInline
                  ? cn(
                      // No visible border / shadow at rest — looks like plain text.
                      'min-h-[2rem] rounded-md border border-transparent bg-transparent px-2 py-1.5 shadow-none',
                      'hover:border-input hover:bg-muted/60',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      'data-[state=open]:border-primary/50 data-[state=open]:bg-muted/40 data-[state=open]:ring-2 data-[state=open]:ring-ring',
                      'disabled:cursor-not-allowed',
                    )
                  : cn(
                      'h-10 rounded-lg border border-input bg-background pl-3.5 pr-2 leading-none shadow-sm',
                      'hover:border-primary/40 hover:bg-muted/40',
                      'focus-visible:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      'data-[state=open]:border-primary/60 data-[state=open]:ring-2 data-[state=open]:ring-ring',
                      'disabled:cursor-not-allowed',
                    ),
                className,
              )}
            >
              <span className="flex min-w-0 flex-1 items-center">{displayLabel}</span>
              {isInline ? (
                <ChevronsUpDown
                  className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 peer-data-[state=open]:opacity-100"
                  aria-hidden
                />
              ) : (
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted/60 text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary peer-data-[state=open]:bg-primary/10 peer-data-[state=open]:text-primary"
                  aria-hidden
                >
                  <ChevronsUpDown className="h-3.5 w-3.5" />
                </span>
              )}
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              sideOffset={6}
              collisionPadding={8}
              className={cn(
                'z-50 max-h-[min(22rem,60vh)] w-[var(--radix-dropdown-menu-trigger-width)] min-w-[10rem] overflow-y-auto overflow-x-hidden rounded-xl border border-border/70 bg-popover p-1.5 text-popover-foreground shadow-xl ring-1 ring-black/5',
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
              )}
            >
              {options.length === 0 ? (
                <div className="px-3 py-2 text-xs text-muted-foreground">No options</div>
              ) : (
                options.map((opt) => {
                  const isSelected = opt.value === current;
                  return (
                    <DropdownMenu.Item
                      key={opt.key}
                      disabled={opt.disabled ?? false}
                      onSelect={(e) => {
                        if (opt.disabled) {
                          e.preventDefault();
                          return;
                        }
                        select(opt.value);
                      }}
                      className={cn(
                        'relative flex min-h-[2.25rem] cursor-pointer select-none items-center gap-2 rounded-md py-1.5 pl-8 pr-3 text-sm outline-none transition-colors',
                        'focus:bg-accent focus:text-accent-foreground',
                        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
                        'data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
                        isSelected ? 'bg-primary/10 font-medium text-primary' : '',
                      )}
                    >
                      <span className="absolute left-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
                        {isSelected ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
                      </span>
                      <span className="flex min-w-0 flex-1 items-center">
                        {opt.label === '' || opt.label === undefined || opt.label === null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          opt.label
                        )}
                      </span>
                    </DropdownMenu.Item>
                  );
                })
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    );
  },
);
Select.displayName = 'Select';
